import React, { useMemo } from 'react';
import { AlertTriangle, CheckCircle2, Info, ShieldAlert } from 'lucide-react';
import { SaveCancelBar } from '../ui/SaveCancelBar';

const STATUS_META = {
  ok: {
    icon: CheckCircle2,
    badgeClass: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    iconClass: 'text-emerald-600',
    label: 'OK'
  },
  warning: {
    icon: AlertTriangle,
    badgeClass: 'bg-amber-50 text-amber-800 border-amber-200',
    iconClass: 'text-amber-600',
    label: 'Atenção'
  },
  error: {
    icon: ShieldAlert,
    badgeClass: 'bg-rose-50 text-rose-700 border-rose-200',
    iconClass: 'text-rose-600',
    label: 'Crítico'
  },
  info: {
    icon: Info,
    badgeClass: 'bg-sky-50 text-sky-700 border-sky-200',
    iconClass: 'text-sky-600',
    label: 'Info'
  }
};

const normalizeArray = (value) => {
  if (Array.isArray(value)) return value.filter(Boolean);
  if (value === null || value === undefined || value === '') return [];
  return [value].filter(Boolean);
};

const clean = (value) => String(value || '').trim();

const hasAnyValue = (values) => normalizeArray(values).some((value) => clean(value).length > 0);

const formatList = (values) => {
  const arr = normalizeArray(values).map((value) => clean(value)).filter(Boolean);
  return arr.length ? arr.join(', ') : 'Não preenchido';
};

const buildContractChecklist = (data) => {
  const parties = Array.isArray(data?.parties) ? data.parties : [];
  const linkedParties = parties.filter((party) => clean(party?.clientId || party?.qualificationData?.id));
  const primaryParty = parties[0] || {};
  const ramoDireito = clean(data?.ramoDireito);
  const posicaoCliente = clean(data?.posicaoCliente);
  const objetoPrincipal = clean(data?.objetoPrincipal);
  const selectedHonorarios = normalizeArray(data?.selectedHonorarios);
  const customHonorarios = normalizeArray(data?.customHonorarios);
  const valorCausa = Number(data?.valorCausa || 0);
  const valorHonorarios = Number(data?.valorHonorarios || 0);
  const percentualExito = Number(data?.percentualExito || 0);
  const hasHonorarios = selectedHonorarios.length > 0 || customHonorarios.length > 0 || valorHonorarios > 0 || percentualExito > 0;
  const primaryClientId = clean(primaryParty?.clientId || primaryParty?.qualificationData?.id);

  return [
    {
      key: 'crm_link',
      label: 'Parte principal vinculada ao CRM',
      status: primaryClientId ? 'ok' : 'error',
      value: primaryClientId ? clean(primaryParty?.name || 'Vinculada') : 'A publicação exige clientId na parte principal.'
    },
    {
      key: 'parties',
      label: 'Partes do contrato',
      status: linkedParties.length > 0 ? 'ok' : 'warning',
      value: linkedParties.length > 0 ? `${linkedParties.length} parte(s) vinculada(s)` : 'Sem partes vinculadas ao CRM'
    },
    {
      key: 'ramo',
      label: 'Ramo do Direito',
      status: ramoDireito ? 'ok' : 'warning',
      value: ramoDireito || 'Não preenchido'
    },
    {
      key: 'posicao',
      label: 'Posição do cliente',
      status: posicaoCliente ? 'ok' : 'warning',
      value: posicaoCliente || 'Não preenchido'
    },
    {
      key: 'objeto',
      label: 'Objeto principal',
      status: objetoPrincipal ? 'ok' : 'warning',
      value: objetoPrincipal || 'Não preenchido'
    },
    {
      key: 'honorarios',
      label: 'Honorários e condições financeiras',
      status: hasHonorarios ? 'ok' : 'warning',
      value: hasHonorarios
        ? [
            selectedHonorarios.length ? `Tipos: ${formatList(selectedHonorarios)}` : '',
            customHonorarios.length ? `Custom: ${formatList(customHonorarios)}` : '',
            valorHonorarios > 0 ? `Valor fixo: R$ ${valorHonorarios.toFixed(2)}` : '',
            percentualExito > 0 ? `Êxito: ${percentualExito}%` : ''
          ].filter(Boolean).join(' | ')
        : 'Nenhum honorário ou condição financeira identificado'
    },
    {
      key: 'valor_causa',
      label: 'Valor da causa',
      status: valorCausa > 0 ? 'ok' : 'info',
      value: valorCausa > 0 ? `R$ ${valorCausa.toFixed(2)}` : 'Não informado'
    }
  ];
};

const buildProcurationChecklist = (data) => {
  const parties = Array.isArray(data?.parties) ? data.parties : [];
  const primaryParty = parties[0] || {};
  const primaryClientId = clean(primaryParty?.clientId || primaryParty?.qualificationData?.id);
  const ramosDireito = normalizeArray(data?.ramosDireito?.length ? data.ramosDireito : data?.ramoDireito).map((value) => clean(value)).filter(Boolean);
  const posicoesCliente = normalizeArray(data?.posicoesCliente?.length ? data.posicoesCliente : data?.posicaoCliente).map((value) => clean(value)).filter(Boolean);
  const objetoPrincipal = clean(data?.objetoPrincipal);
  const isPenal = ramosDireito.includes('Penal') || clean(data?.ramoDireito) === 'Penal';
  const quereladoName = clean(data?.quereladoName);
  const dataFato = clean(data?.dataFato);
  const patrimonialEnabled = Boolean(data?.patrimonialImobiliarioEnabled);
  const validadeTipo = clean(data?.validadeMandatoTipo);
  const validadeData = clean(data?.validadeMandatoData);
  const validadeAto = clean(data?.validadeMandatoAto);

  return [
    {
      key: 'crm_link',
      label: 'Outorgante principal vinculado ao CRM',
      status: primaryClientId ? 'ok' : 'error',
      value: primaryClientId ? clean(primaryParty?.name || 'Vinculado') : 'A publicação exige clientId no outorgante principal.'
    },
    {
      key: 'parties',
      label: 'Outorgantes',
      status: parties.length > 0 ? 'ok' : 'warning',
      value: parties.length > 0 ? `${parties.length} outorgante(s) no formulário` : 'Nenhum outorgante informado'
    },
    {
      key: 'ramos',
      label: 'Ramos do Direito',
      status: ramosDireito.length > 0 ? 'ok' : 'warning',
      value: ramosDireito.length > 0 ? ramosDireito.join(', ') : 'Não preenchido'
    },
    {
      key: 'posicoes',
      label: 'Posições do cliente',
      status: posicoesCliente.length > 0 ? 'ok' : 'warning',
      value: posicoesCliente.length > 0 ? posicoesCliente.join(', ') : 'Não preenchido'
    },
    {
      key: 'objeto',
      label: 'Objeto principal / ação',
      status: objetoPrincipal ? 'ok' : 'warning',
      value: objetoPrincipal || 'Não preenchido'
    },
    {
      key: 'penal_fact',
      label: 'Campos fáticos penais',
      status: !isPenal ? 'info' : (quereladoName && dataFato ? 'ok' : 'warning'),
      value: !isPenal
        ? 'Checklist penal não aplicável para os ramos selecionados'
        : [quereladoName ? `Querelado: ${quereladoName}` : 'Querelado em branco', dataFato ? `Data do fato: ${dataFato}` : 'Data do fato em branco'].join(' | ')
    },
    {
      key: 'validade',
      label: 'Validade do mandato',
      status: validadeTipo === 'DETERMINADO' ? (validadeData || validadeAto ? 'ok' : 'warning') : 'info',
      value: validadeTipo === 'DETERMINADO'
        ? (validadeData || validadeAto ? [validadeData ? `Data: ${validadeData}` : '', validadeAto ? `Ato: ${validadeAto}` : ''].filter(Boolean).join(' | ') : 'Mandato determinado sem data ou ato de validade')
        : (validadeTipo || 'Indeterminado')
    },
    {
      key: 'patrimonial',
      label: 'Bloco patrimonial imobiliário',
      status: patrimonialEnabled ? (hasAnyValue([data?.bensImoveisEspecificos, data?.bemImovelMatricula, data?.bemImovelCartorio, data?.bemImovelEndereco]) ? 'ok' : 'warning') : 'info',
      value: patrimonialEnabled ? 'Ativado' : 'Não ativado'
    }
  ];
};

export const PreFlightCheckModal = ({
  isOpen,
  onClose,
  onConfirm,
  documentType,
  data,
  publishIntent = 'simple',
  extraContent = null
}) => {
  const items = useMemo(() => {
    return documentType === 'procuration'
      ? buildProcurationChecklist(data)
      : buildContractChecklist(data);
  }, [data, documentType]);

  const counts = useMemo(() => {
    return items.reduce((acc, item) => {
      acc[item.status] = (acc[item.status] || 0) + 1;
      return acc;
    }, { ok: 0, warning: 0, error: 0, info: 0 });
  }, [items]);

  const hasBlockingIssues = counts.error > 0;
  const title = documentType === 'procuration' ? 'Auditoria Prévia da Procuração' : 'Auditoria Prévia do Contrato';
  const actionLabel = publishIntent === 'consent' ? 'Confirmar e Solicitar Consentimento' : 'Confirmar e Publicar';

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[1300] flex items-center justify-center p-4 no-print">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative w-full max-w-3xl bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden">
        <div className="px-6 py-5 border-b border-gray-100 bg-gradient-to-r from-[#20243B] to-[#2b3150] text-white">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h3 className="text-lg font-bold">{title}</h3>
              <p className="text-sm text-white/80 mt-1">
                Revisão determinística dos campos críticos antes de disparar o fluxo já existente de publicação.
              </p>
            </div>
            <button type="button" onClick={onClose} className="text-white/70 hover:text-white text-sm font-bold">
              Fechar
            </button>
          </div>
        </div>

        <div className="p-6 space-y-5 max-h-[78vh] overflow-y-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {['ok', 'warning', 'error', 'info'].map((statusKey) => {
              const meta = STATUS_META[statusKey];
              return (
                <div key={statusKey} className={`rounded-xl border px-4 py-3 ${meta.badgeClass}`}>
                  <div className="text-[10px] uppercase font-black tracking-wider">{meta.label}</div>
                  <div className="text-2xl font-black mt-1">{counts[statusKey] || 0}</div>
                </div>
              );
            })}
          </div>

          <div className={`rounded-xl border px-4 py-3 ${hasBlockingIssues ? 'bg-rose-50 border-rose-200 text-rose-700' : 'bg-emerald-50 border-emerald-200 text-emerald-700'}`}>
            <div className="text-xs font-bold uppercase tracking-wider">
              {hasBlockingIssues ? 'Bloqueio de Publicação' : 'Pronto para seguir'}
            </div>
            <div className="text-sm mt-1">
              {hasBlockingIssues
                ? 'Existem itens críticos que impedem o disparo do fluxo. Corrija os campos marcados como críticos.'
                : 'Nenhum bloqueio crítico encontrado. Você ainda verá a confirmação nativa do motor antes da publicação.'}
            </div>
          </div>

          <div className="space-y-3">
            {items.map((item) => {
              const meta = STATUS_META[item.status] || STATUS_META.info;
              const Icon = meta.icon;
              return (
                <div key={item.key} className="rounded-xl border border-gray-200 p-4 bg-white shadow-sm">
                  <div className="flex items-start gap-3">
                    <Icon className={`w-5 h-5 mt-0.5 ${meta.iconClass}`} />
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-sm font-bold text-[#20243B]">{item.label}</span>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[10px] font-black uppercase tracking-wider ${meta.badgeClass}`}>
                          {meta.label}
                        </span>
                      </div>
                      <div className="text-sm text-gray-600 mt-1 whitespace-pre-wrap break-words">{item.value}</div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {extraContent ? (
            <div className="mt-4">{extraContent}</div>
          ) : null}
        </div>

        <div className="px-6 py-4 border-t border-gray-100 bg-gray-50">
          <SaveCancelBar
            onCancel={onClose}
            onSave={onConfirm}
            cancelLabel="Cancelar"
            saveLabel={actionLabel}
            disabledSave={hasBlockingIssues}
          />
        </div>
      </div>
    </div>
  );
};

export default PreFlightCheckModal;
